/* The bookstore assistant - the shop's search, wearing a person.
 *
 * She is the same conversation everywhere: the corner bust medallion in the
 * Long Room and the little portrait in the corner of a shelf page are one
 * continuous chat, carried between page loads in sessionStorage. So a
 * shopper can ask for a book, walk to the shelf she sends them to, and say "no,
 * something lighter" without starting again. (BW-114 rework: she used to stand
 * full length in the room, painted into the scene art with a click-area over
 * her - that composite drifted from her reference figure on regen, so she is
 * the bust everywhere now, mode "mini" only. BW-352: she is Trish - see the
 * `faces`/`setExpression` block below for her expression state machine.)
 *
 * Self-contained on purpose. The walkable scene loads walkable.js; the
 * collection and search pages do not. Both mount this.
 *
 * Talking happens at POST <endpoint> (the Bookworms app). She answers with what
 * she says, the real books a search found, and at most one shelf worth walking
 * to. Everything she names has been checked server-side against what the shop
 * actually has, so this file may render her answer without second-guessing it.
 */
(function () {
  "use strict";

  var STORE_KEY = "bwAssistantChat";     // the conversation, across page loads
  var SEEN_KEY = "bwAssistantShelf";     // the last shelf we reported back to her
  var HINT_KEY = "bwAssistantHinted";    // has she already pulsed once this session
  var OUTFIT_KEY = "bwAssistantOutfit";  // BW-352: which of her three outfits, for this session
  var HINT_MS = 1000;                    // >= the 900ms CSS animation
  var MAX_TURNS = 12;
  var TIMEOUT_MS = 20000;   // 20s per the design. The 40s testing-phase value is gone: the Azure face
                             // now runs min_replicas 1 (no scale-from-zero cold start, BW-179/BW-181)
                             // and answers in 3-7s on the live store (BW-272)

  // In-character replies for the ways she can fail to answer at all, keyed by
  // the `kind` post() classifies the error into. "http" keeps today's
  // server-authored message (or the fallback below) rather than one of these.
  var TROUBLE = {
    timeout: "She is still somewhere in the stacks — nothing came back in time. Ask her again in a moment?",
    network: "She cannot hear you from here; the line to the shop has gone quiet. Try again shortly.",
    rate: "She can only chase so many books at once, and she has been run off her feet. Give her a little while and ask again."
  };

  var COOLDOWN_MS = 10000;   // TIMEOUT only - a 10s cooldown against an hourly rate limit is theatre

  // BW-179 decision 9, reworded for Trish (BW-352, legal's final copy): a
  // plain AI disclosure in the panel itself, kept in one constant so a future
  // revision is a one-line swap.
  var AI_NOTE = "Trish is our AI bookseller. She reads the shelves keenly, but do check the details.";

  // ---- BW-352: expressions -------------------------------------------------
  // The seven faces the model can ask for, and the outfit letters her portrait
  // rotates through once per session. Both lists are the single source of
  // truth the widget validates against - never trust the string a chat reply
  // carries without checking it against EXPRESSIONS first.
  var EXPRESSIONS = ["listening", "smiling", "laughing", "thinking", "puzzled", "sorry", "delighted"];
  var OUTFITS = ["a", "b", "c"];
  var DEFAULT_EXPRESSION = "smiling";
  var EXPRESSION_HOLD_MS = 6000;   // a returned expression is held this long, then back to smiling
  var LISTENING_IDLE_MS = 4000;    // typing with no further keystroke this long reverts to smiling

  function el(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  }

  // Text from the model goes in as text, never as markup.
  function setText(node, text) { node.textContent = text == null ? "" : text; }

  function portraitImg(cls, src) {
    var img = el("img", "ws-assist-img " + cls);
    img.src = src; img.alt = ""; img.draggable = false;
    return img;
  }

  function load() {
    try { return JSON.parse(sessionStorage.getItem(STORE_KEY)) || []; }
    catch (e) { return []; }
  }
  function save(messages) {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(messages.slice(-MAX_TURNS))); }
    catch (e) { /* private mode: she simply forgets between pages */ }
  }

  // ---- BW-352: which of her three outfits, once per session. sessionStorage
  // makes "fixed for the conversation" true by construction, beside the
  // existing bwAssistantChat key. Falls back to a page-lifetime variable if
  // storage throws (private mode) - same graceful-forgetting shape as the
  // chat transcript above, just re-rolled on the next page instead of kept.
  var pageOutfit = null;
  function pickOutfit() {
    try {
      var stored = sessionStorage.getItem(OUTFIT_KEY);
      if (stored && OUTFITS.indexOf(stored) !== -1) return stored;
      var picked = OUTFITS[Math.floor(Math.random() * OUTFITS.length)];
      sessionStorage.setItem(OUTFIT_KEY, picked);
      return picked;
    } catch (e) {
      if (!pageOutfit) pageOutfit = OUTFITS[Math.floor(Math.random() * OUTFITS.length)];
      return pageOutfit;
    }
  }

  function mount(opts) {
    var endpoint = opts.endpoint;
    var messages = load();
    var busy = false;
    var root = el("div", "ws-assist");

    // ---- BW-352: her face table for this session's outfit, and the
    // expression state machine. `faces` comes from Liquid
    // (window.BW_ASSISTANT.faces / the homepage's DATA.assistantFaces) -
    // never built from a model-authored string.
    var outfit = pickOutfit();
    var faces = (opts.faces && opts.faces[outfit]) || null;
    var loadedFrames = {};
    var expressionTimer = null;
    var listeningTimer = null;   // BW-352 follow-up: typing detection, separate from the reply hold above

    function faceUrl(name) {
      return (faces && faces[name]) || null;
    }

    // Validates against the fixed EXPRESSIONS list BEFORE any lookup - never
    // index `faces` with an unchecked string (a plain object answers for
    // "constructor"/"toString"/"__proto__"). Swaps only `src`; never derives
    // a class name, `alt` or `aria-label` from the value.
    function setExpression(name) {
      if (EXPRESSIONS.indexOf(name) === -1) name = DEFAULT_EXPRESSION;
      var url = faceUrl(name);
      if (!url) return;   // no faces table at all (mismatched theme/app pairing) - stay put
      medallion.src = url;
      panelFace.src = url;
    }

    // A returned (or "sorry") expression is held, then falls back to
    // smiling - her resting face once she has actually spoken.
    function holdExpression(name) {
      if (expressionTimer) { clearTimeout(expressionTimer); expressionTimer = null; }
      setExpression(name);
      expressionTimer = setTimeout(function () {
        expressionTimer = null;
        setExpression("smiling");
      }, EXPRESSION_HOLD_MS);
    }

    // BW-352 follow-up: "listening" shows only while the shopper is actively
    // typing - a keystroke starts (or restarts) it, and it holds only as
    // long as keystrokes keep arriving; idle for LISTENING_IDLE_MS with none
    // reverts to smiling on its own. A running reply-hold or an in-flight
    // fetch outranks it, same precedence open()/close() give those. Focus
    // alone does NOT trigger it - open() already focuses the input as an
    // accessibility default, and that would show listening on every open
    // with nothing typed.
    function clearListeningTimer() {
      if (listeningTimer) { clearTimeout(listeningTimer); listeningTimer = null; }
    }
    function startListening() {
      if (busy || expressionTimer) return;
      clearListeningTimer();
      setExpression("listening");
      listeningTimer = setTimeout(function () {
        listeningTimer = null;
        setExpression("smiling");
      }, LISTENING_IDLE_MS);
    }
    function stopListening() {
      clearListeningTimer();
      if (!busy && !expressionTimer) setExpression("smiling");
    }

    // Preloads this session's other six frames (about 90KiB) once idle, so a
    // later setExpression() swap never shows a half-loaded image. Never
    // preloads the other two outfits.
    function preloadOutfit() {
      if (!faces) return;
      EXPRESSIONS.forEach(function (name) {
        if (name === DEFAULT_EXPRESSION || loadedFrames[name]) return;
        var url = faces[name];
        if (!url) return;
        var img = new Image();
        img.onload = function () { loadedFrames[name] = true; };
        img.src = url;
      });
    }
    if (window.requestIdleCallback) window.requestIdleCallback(preloadOutfit);
    else setTimeout(preloadOutfit, 200);

    // ---- her: a portrait you can click, and what she last said.
    var figure = el("button", "ws-assist-fig");
    figure.type = "button";
    figure.setAttribute("aria-label", "Ask Trish, our AI bookseller");
    var medallion = portraitImg("ws-assist-img--bust", faceUrl(DEFAULT_EXPRESSION) || "");
    figure.appendChild(medallion);
    // BW-352 follow-up: the visible tag stays short (the always-on mobile/touch
    // state has no room for the full phrase) but keeps the AI marker on the
    // pre-open surface, per the security review; the full sentence lives only
    // in the aria-label above.
    figure.appendChild(el("span", "ws-assist-tag", "Ask Trish · AI"));
    root.appendChild(figure);

    // The hail: one line of her, floating by her head, before you have opened
    // anything. It is how a shopper learns she can be spoken to at all.
    var hail = el("div", "ws-assist-hail");
    setText(hail, opts.greeting || "Looking for anything in particular?");
    root.appendChild(hail);

    // ---- the conversation panel
    var panel = el("div", "ws-assist-panel");
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-label", "The bookstore assistant");
    panel.innerHTML =
      '<div class="ws-assist-head">' +
        '<img class="ws-assist-face" alt="">' +
        '<div><div class="ws-assist-name">Trish</div>' +
        '<div class="ws-assist-role">AI bookseller</div></div>' +
        '<button class="ws-assist-close" aria-label="Close">&times;</button>' +
      '</div>' +
      '<div class="ws-assist-ai"></div>' +
      '<div class="ws-assist-log" aria-live="polite"></div>' +
      '<div class="ws-assist-chips"></div>' +
      '<form class="ws-assist-form">' +
        '<input class="ws-assist-in" type="text" maxlength="500" autocomplete="off" ' +
          'placeholder="An author, a subject, or a mood…" aria-label="Ask Trish">' +
        '<button class="ws-assist-send" type="submit">Ask</button>' +
      '</form>';
    root.appendChild(panel);
    var panelFace = panel.querySelector(".ws-assist-face");
    panelFace.src = faceUrl(DEFAULT_EXPRESSION) || "";
    setText(panel.querySelector(".ws-assist-ai"), AI_NOTE);

    var log = panel.querySelector(".ws-assist-log");
    var chips = panel.querySelector(".ws-assist-chips");
    var form = panel.querySelector(".ws-assist-form");
    var input = panel.querySelector(".ws-assist-in");
    var send = panel.querySelector(".ws-assist-send");

    (opts.host || document.body).appendChild(root);

    // ---- rendering the conversation
    function bubble(role, text) {
      var b = el("div", "ws-assist-msg ws-assist-msg--" + role);
      setText(b, text);
      log.appendChild(b);
      return b;
    }

    // Real books, from a real search. Clicking one opens its product page.
    function shelf(books) {
      var strip = el("div", "ws-assist-books");
      books.forEach(function (bk) {
        var card = el("a", "ws-assist-book");
        card.href = bk.url;
        if (bk.image) {
          var img = el("img"); img.src = bk.image; img.alt = ""; img.loading = "lazy";
          card.appendChild(img);
        }
        var meta = el("span", "ws-assist-bmeta");
        var t = el("span", "ws-assist-bt"); setText(t, bk.title); meta.appendChild(t);
        if (bk.author) { var a = el("span", "ws-assist-ba"); setText(a, bk.author); meta.appendChild(a); }
        // Already currency-prefixed by the backend (NZ$10.39, or "USD 9.99"). Never prepend a symbol.
        if (bk.price) { var p = el("span", "ws-assist-bp"); setText(p, bk.price); meta.appendChild(p); }
        card.appendChild(meta);
        strip.appendChild(card);
      });
      log.appendChild(strip);
    }

    // The way to the shelf she named. A carved plank, as everywhere else.
    function walkTo(search) {
      var go = el("button", "ws-assist-walk");
      go.type = "button";
      setText(go, "Take me to " + search.label + " →");
      go.addEventListener("click", function () { follow(search); });
      log.appendChild(go);
    }

    function follow(search) {
      close();
      if (opts.onSearch && opts.onSearch(search)) return;   // handled in-world
      var url = search.kind === "collection"
        ? "/collections/" + encodeURIComponent(search.handle)
        : "/search?type=product&q=" + encodeURIComponent(search.q);
      window.location.href = url;
    }

    function renderChips(list) {
      chips.innerHTML = "";
      (list || []).forEach(function (text) {
        var chip = el("button", "ws-assist-chip");
        chip.type = "button";
        setText(chip, text);
        chip.addEventListener("click", function () { ask(text); });
        chips.appendChild(chip);
      });
    }

    function scroll() { log.scrollTop = log.scrollHeight; }

    function replay() {
      log.innerHTML = "";
      messages.forEach(function (m) { bubble(m.role, m.content); });
    }

    // ---- talking to her
    function say(reply) {
      bubble("assistant", reply.say);
      if (reply.books && reply.books.length) shelf(reply.books);
      if (reply.search) walkTo(reply.search);
      renderChips(reply.chips);
      scroll();
    }

    // Classifies a failure so the caller can pick an in-character reply
    // rather than surface raw fetch/HTTP text. `err.kind` is one of
    // "timeout" / "network" / "rate" / "http"; `err.message` carries the http
    // case's server-authored (or fallback) text. 429 gets its own in-character
    // line ahead of the generic http branch; every other status keeps the
    // server-authored detail.
    function post(body) {
      var ctl = new AbortController();
      var t = setTimeout(function () { ctl.abort(); }, TIMEOUT_MS);
      return fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: ctl.signal
      }).then(function (v) { clearTimeout(t); return v; }, function (e) { clearTimeout(t); throw e; })
        .then(function (r) {
          if (r.ok) return r.json();
          if (r.status === 429) { var e429 = new Error(); e429.kind = "rate"; throw e429; }
          return r.json().catch(function () { return {}; }).then(function (j) {
            var err = new Error(j.detail || "She is not answering just now.");
            err.kind = "http";
            throw err;
          });
        })
        .catch(function (e) {
          if (!e.kind) e.kind = (e.name === "AbortError" || ctl.signal.aborted) ? "timeout" : "network";
          throw e;
        });
    }

    // A brief, visible cooldown on the send button after a TIMEOUT only - not
    // a 429, where the real limit is per-hour and a 10s cooldown would be
    // theatre. The input stays enabled for typing; only the button (and Enter,
    // via the submit handler below) is inert while cooling.
    var cooldownTimer = null;
    function startCooldown() {
      if (cooldownTimer) return;
      var remaining = Math.round(COOLDOWN_MS / 1000);
      send.disabled = true;
      setText(send, "Wait " + remaining);
      cooldownTimer = setInterval(function () {
        remaining -= 1;
        if (remaining <= 0) {
          clearInterval(cooldownTimer);
          cooldownTimer = null;
          send.disabled = false;
          setText(send, "Ask");
        } else {
          setText(send, "Wait " + remaining);
        }
      }, 1000);
    }

    // A turn. `shelf` (optional) tells her what the shopper is actually looking
    // at, which is the only way "nothing on those shelves" can be a real answer.
    function turn(userText, shelfState) {
      if (busy) return;
      busy = true;
      root.classList.add("is-open", "is-busy");
      renderChips([]);
      if (expressionTimer) { clearTimeout(expressionTimer); expressionTimer = null; }
      clearListeningTimer();   // BW-352 follow-up: a submit ends typing state outright
      setExpression("thinking");   // BW-352: a fetch in flight is "thinking"
      if (userText) { messages.push({ role: "user", content: userText }); bubble("user", userText); }
      var thinking = bubble("assistant", "…");
      thinking.classList.add("is-thinking");
      scroll();

      post({ messages: messages.slice(-MAX_TURNS), shelf: shelfState || null })
        .then(function (reply) {
          thinking.remove();
          messages.push({ role: "assistant", content: reply.say });
          save(messages);
          holdExpression(reply.expression);   // tolerates a missing/unknown value
          say(reply);
        })
        .catch(function (err) {
          thinking.remove();
          var trouble = TROUBLE[err.kind];
          var msg = trouble || err.message || "She is not answering just now.";
          bubble("assistant", msg).classList.add("ws-assist-msg--trouble");
          holdExpression("sorry");
          if (err.kind === "timeout") startCooldown();
        })
        .then(function () {
          busy = false;
          root.classList.remove("is-busy");
        });
    }

    function ask(text) {
      text = (text || "").trim();
      if (text) turn(text, null);
    }

    // ---- opening and closing
    // Trap Tab inside the panel ONLY when it is genuinely modal: a full-width
    // sheet on phones (assistant.css's 760px breakpoint). On desktop she is
    // always a non-modal corner widget - trapping there would strand a
    // keyboard shopper away from the shelf behind her, which is worse than
    // not trapping at all. Initial focus and restore-on-close happen either
    // way; only the Tab cycling is conditional.
    var panelTrap = null;
    function open() {
      root.classList.add("is-open");
      // BW-365: hides the Shopify Inbox launcher pill on a phone while this
      // panel is open, so the two never overlap (assistant.css).
      document.body.classList.add("bw-assist-chat-open");
      if (!messages.length && !log.children.length) bubble("assistant", opts.greeting);
      scroll();
      // BW-352 follow-up: opening (or re-opening) with nothing in flight is
      // "smiling" - her resting face - not "listening", which is now reserved
      // for active typing. A running hold timer from a just-finished turn
      // takes precedence.
      if (!busy && !expressionTimer) setExpression("smiling");
      // report() can call open() while the panel is already open (a shelf
      // with 0 or >80 results) - release any existing trap first, or its
      // keydown listener leaks forever and Tab stays stuck in the panel.
      if (panelTrap) { panelTrap.release(); panelTrap = null; }
      var modal = window.matchMedia("(max-width: 760px)").matches;
      if (modal) panelTrap = window.BookwormsFocus.trap(panel, { initial: input, restore: figure });
      else input.focus();
    }
    function close() {
      root.classList.remove("is-open");
      // BW-365: undoes open()'s body-class toggle so the Shopify Inbox
      // launcher pill reappears once this panel is closed.
      document.body.classList.remove("bw-assist-chat-open");
      // BW-352 follow-up: closed rests on "smiling" too - don't leave her
      // frozen on whatever her last reply's expression was, and don't leave
      // her "listening" once the shopper isn't typing at all.
      if (expressionTimer) { clearTimeout(expressionTimer); expressionTimer = null; }
      clearListeningTimer();
      if (!busy) setExpression("smiling");
      if (panelTrap) { panelTrap.release(); panelTrap = null; }
      else { figure.focus(); }
    }

    // She is a portrait in the corner everywhere she appears. A null mode means
    // she is not in this scene at all - out on the street, she is inside
    // minding the shop.
    function setMode(mode) {
      root.classList.toggle("ws-assist--mini", mode === "mini");
      root.style.display = mode ? "" : "none";
      if (!mode) close();
    }
    // `mode: null` means "not in this scene" - so it must not fall through to a
    // default. Only an absent mode does.
    setMode(opts.mode === undefined ? "mini" : opts.mode);

    figure.addEventListener("click", function () {
      root.classList.contains("is-open") ? close() : open();
    });
    panel.querySelector(".ws-assist-close").addEventListener("click", close);
    // BW-352 follow-up: "listening" while she's actually being typed at.
    // input/keydown both start (or restart) it so any keystroke keeps her
    // listening; focus alone does not (see startListening's comment above -
    // open() already focuses this field, and that isn't typing); blur ends
    // it immediately; idle for LISTENING_IDLE_MS with no further keystroke
    // ends it too (startListening's own timeout).
    input.addEventListener("input", startListening);
    input.addEventListener("keydown", startListening);
    input.addEventListener("blur", stopListening);
    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      // A disabled send button does not stop Enter in the input - the
      // submit handler is the only place that actually gates a cooling turn.
      if (cooldownTimer) return;
      var text = input.value;
      input.value = "";
      ask(text);
    });
    // Escape belongs to whatever is on top; only take it when we are.
    document.addEventListener("keydown", function (ev) {
      if (ev.key === "Escape" && root.classList.contains("is-open")) {
        ev.stopPropagation();
        close();
      }
    });

    replay();

    // ---- she notices what you found
    // Only worth her breath when the shelf disappoints - empty, or so full it
    // needs narrowing. Reported once per shelf, so walking back and forth does
    // not make her say it again (nor spend another call saying it). The
    // !messages.length check is deliberate too: she does not open a
    // conversation the shopper never started.
    function report(shelfState) {
      if (!shelfState) return;
      var stamp = shelfState.label + "|" + shelfState.count;
      var worth = shelfState.count === 0 || shelfState.count > 80;
      if (!worth || !messages.length || sessionStorage.getItem(SEEN_KEY) === stamp) return;
      try { sessionStorage.setItem(SEEN_KEY, stamp); } catch (e) { /* ignore */ }
      open();
      turn(null, shelfState);
    }
    report(opts.shelf);

    // ---- BW-93: notice her, once
    // A shopper's first look at a shelf/search page is also her first
    // introduction there - the label (always visible now) already names her,
    // this just draws the eye once. sessionStorage caps it at one pulse per
    // session; reduced motion drops the animation entirely and leans on the
    // always-visible label instead. Never opens the panel or steals focus.
    if (opts.mode === "mini") {
      try {
        if (!sessionStorage.getItem(HINT_KEY)) {
          sessionStorage.setItem(HINT_KEY, "1");
          var reduceMotion = window.matchMedia &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;
          if (!reduceMotion) {
            root.classList.add("is-hinting");
            setTimeout(function () { root.classList.remove("is-hinting"); }, HINT_MS);
          }
        }
      } catch (e) { /* private mode: she just never gets her one pulse */ }
    }

    return { root: root, open: open, close: close, ask: ask, report: report, setMode: setMode };
  }

  // ---- BW-365 follow-up: only name the Inbox launcher when it is really
  // there -------------------------------------------------------------
  // The launcher (a <shopify-chat> host the Inbox app embed injects) may not
  // be in the DOM yet when this script runs - the embed can inject after
  // load. Runs once, independent of mount(), so the tag still appears on a
  // page that has the markup but never mounts Trish (there is none today,
  // but nothing here should assume otherwise). Never a theme setting: this
  // way the tag can't drift out of sync with Inbox actually being
  // installed in Shopify admin - assistant.css gates `.bw-inbox-tag` on
  // this class so it never names a launcher that isn't there.
  function markInboxPresent() { document.body.classList.add("bw-inbox-present"); }
  if (document.querySelector("shopify-chat")) {
    markInboxPresent();
  } else if (typeof customElements !== "undefined" && customElements.whenDefined) {
    customElements.whenDefined("shopify-chat").then(markInboxPresent, function () {});
  }

  window.BookwormsAssistant = { mount: mount };
})();
